import { useEffect, useState } from 'react';
import { Loading, Select, Spacer, Toggle } from '@roc-digital/ui-web';
import { useClickEvent } from '@roc-digital/ui-lib';
import { SessionState } from '@roc-digital/mxm-base/state';
import { User, UserSchema } from '@roc-digital/mxm-base/data';
import { debounce, readUser, updateUser } from '@roc-digital/mxm-base/logic';
import { toast } from 'react-toastify';

interface ExtUser extends User {
  notification_breaking?: boolean;
  notification_am_pm?: boolean;
  push_notification_comments?: string;
  email_notification_comments?: string;
}

interface CommentState {
  enabled?: boolean;
  email?: string;
  push?: string;
}

const updateDebounce = debounce(async (args) => {
  const userData = await updateUser(SessionState, ...args);

  SessionState.setUser(userData);
}, 1200) as (changes: Partial<UserSchema>) => void;

const Notifications = () => {
  const [breakingNews, setBreakingNews] = useState(false);

  const [commentState, setCommentState] = useState<CommentState>({});
  const changeCommentState = (state: CommentState) => {
    setCommentState(current => {
      const next = {...current, ...state};
      if(!current.enabled && next.enabled) {
        next.email = 'all';
        next.push = 'all';
      }
      updateDebounce({
        email_notification_comments: next.enabled ? (next.email || '') : 'none',
        push_notification_comments: next.enabled ? (next.push || '') : 'none',
      })
      return next;
    })
  }
  

  const [amPm, setAmPm] = useState(false);
  const [loading, setLoading] = useState(true);

  const initialize = async () => {
    try {
      setLoading(true);
      const userData = await readUser();

      await SessionState.setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to read user: ');
      console.error(error);
      setLoading(false);
    }
    const userData: ExtUser = (await SessionState.getUser()) as ExtUser;

    if (userData) {
      setBreakingNews(userData.notification_breaking || false);
      setAmPm(userData.notification_am_pm || false);
      const email = userData.email_notification_comments || 'all';
      const push = userData.push_notification_comments || 'all';
      setCommentState({
        email,
        push,
        enabled: email !== 'none' || push !== 'none'
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const clickEvent = useClickEvent(
    () => {
      console.log('Button clicked');
    },
    { namespace: 'mxm.toggle' }
  );

  const onChangeBreakingNews = async (toggle: boolean) => {
    try {
      setLoading(true);
      setBreakingNews(toggle);
      const userData = await updateUser(SessionState, { notification_breaking: toggle });

      SessionState.setUser(userData);
      setLoading(false);
      toast.success('Notification settings updated.');
    } catch (error) {
      console.error('Failed to update user: ');
      console.error(error);
      setLoading(true);
    }
  };

  const onChangeAmPm = async (toggle: boolean) => {
    try {
      setLoading(true);
      setAmPm(toggle);
      const userData = await updateUser(SessionState, { notification_am_pm: toggle });

      SessionState.setUser(userData);
      setLoading(false);
      toast.success('Notification settings updated.');
    } catch (error) {
      console.error('Failed to update user: ');
      console.error(error);
      setLoading(true);
    }
  };

  return (
    <div className="flex flex-col max-w-2xl w-full text-sm dark:text-white">
      <div className="text-2xl font-bold mb-6">Manage Notifications</div>
      <div className="max-w-md text-font-low mb-3">
        Never miss a big story. Get updates from our curators about the news that matters to you.
      </div>
      {loading && <Loading />}
      {!loading && (
        <div className="flex flex-col gap-4 pr-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold">News Updates</div>
              <div className="text-font-low">New and important stories</div>
            </div>
            <Toggle onToggle={onChangeBreakingNews} initialState={breakingNews} clickEvent={clickEvent} />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold">Comments</div>
                <div className="text-font-low">Get updates when others comment on the same articles as you.</div>
              </div>
              <Toggle onToggle={(value) => changeCommentState({enabled: value})} initialState={commentState.enabled} clickEvent={clickEvent} />
            </div>

            {commentState.enabled ? <>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-font-low">Send Email Notifications</div>
                </div>
                <Select
                  changeEvent={(value) => changeCommentState({email: value})}
                  value={commentState.email || 'all'}
                  size="x-small"
                  options={[
                    { label: 'Any Comment', value: 'all' },
                    { label: 'Only Replies', value: 'replies' },
                    { label: 'None', value: 'none' },
                  ]}
                />
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-font-low">Send Phone Notifications</div>
                </div>
                <Select
                  changeEvent={(value) => changeCommentState({push: value})}
                  value={commentState.push || 'all'}
                  size="x-small"
                  options={[
                    { label: 'Any Comment', value: 'all' },
                    { label: 'Only Replies', value: 'replies' },
                    { label: 'None', value: 'none' },
                  ]}
                />
              </div>
            </> : null}
          </div>
        </div>
      )}
    </div>
  );
};

Notifications.displayName = 'Notifications';

export default Notifications;
